import { defineStore } from 'pinia';
import { onRequestDefaultError, requests } from '🔗/utils/requests';

    
const defaultDictEvents = (state, type) => ({
    all: async(params = {}, requestCode) => {
        if (state['all' + type]) {
            return state['all' + type];
        }

        // ToDo
        const items = await state.list({ ...params, skip: 0, take: 100 }, requestCode, type);
        state['all' + type] = items;

        return state['all' + type];
    },
    list: async(params = {}, requestCode) => await state.list(params, requestCode, type),
    add: async(params = {}) => await state.add(params, type),
    get: async(params = {}) => await state.get(params, type),
    update: async(params = {}) => await state.update(params, type),
    remove: async(params = {}) => await state.remove(params, type),
    defaultCover: () => state.defaultCover(type)
});

const useDictApi = defineStore('dict-api', {
    state: () => ({
        types: [],
        viewModes: {
            genres: ['list', 'dashboard'],
            moods: ['list', 'dashboard'],
            categories: ['list', 'dashboard'],
            tags: ['list'],
            instruments: ['list']
        }
    }),
    getters: {
        genres: (state) => defaultDictEvents(state, 'genres'),
        instruments: (state) => defaultDictEvents(state, 'instruments'),
        moods: (state) => defaultDictEvents(state, 'moods'),
        tags: (state) => defaultDictEvents(state, 'tags'),
        categories: (state) => defaultDictEvents(state, 'categories')
    },
    actions: {
        // Base
        defaultCover(type) {
            if (type === 'genres') {
                return {
                    resized: new URL(`🔗/assets/img/genres/resized.jpg`, import.meta.url).href,
                    original: new URL(`🔗/assets/img/genres/original.jpg`, import.meta.url).href
                }
            }

            if (type === 'moods') {
                return {
                    resized: new URL(`🔗/assets/img/moods/resized.jpg`, import.meta.url).href,
                    original: new URL(`🔗/assets/img/moods/original.jpg`, import.meta.url).href
                }
            }

            if (type === 'categories') {
                return {
                    resized: new URL(`🔗/assets/img/categories/resized.jpg`, import.meta.url).href,
                    original: new URL(`🔗/assets/img/categories/original.jpg`, import.meta.url).href
                }
            }
        },
        async getTypes(params = {}) {
            if (this.types?.length) {
                return this.types;
            }

            try {
                const result = await this.$http({
                    method: 'GET',
                    url: '/dict/list',
                    params,
                    // signal: requests.signal(`dictionaries-types-list`)
                });

                if (result.status === 200) {
                    const { data } = result;

                    this.types = data?.items || [];
                    return this.types;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async list(params = {}, requestCode, type) {
            try {
                const result = await this.$http({
                    method: 'GET',
                    url: `/dict/${type}`,
                    params,
                    signal: requests.signal(`dictionaries-${type}-${requestCode || ''}`)
                });

                if (result.status === 200) {
                    const { data } = result;
                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async add(data, type) {
            const formData = new FormData();

            formData.append('title', JSON.stringify(data.title));

            if (data.newCover) {
                formData.append('cover', data.newCover);
            }

            try {
                const result = await this.$http({
                    method: 'POST',
                    url: `/dict/${type}/create`,
                    formData,
                    contentType: 'multipart/form-data',
                    signal: requests.signal('dictionary-add')
                });

                if ([200, 201].indexOf(result.status) !== -1) {
                    const { data } = result;

                    return data;
                } else {
                    onRequestDefaultError({ response: result });
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async get(code, type) {
            try {
                const result = await this.$http({
                    method: 'GET',
                    url: `/dict/${type}/${ code }`,
                    signal: requests.signal('dictionary-get', code)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async update(data, type) {
            const formData = new FormData();

            formData.append('title', JSON.stringify(data.title));

            if (data.newCover) {
                formData.append('cover', data.newCover);
            }

            try {
                const result = await this.$http({
                    method: 'POST',
                    url: `/dict/update/${type}/${data.code}`,
                    formData,
                    contentType: 'multipart/form-data',
                    signal: requests.signal('dictionary-update', data.code)
                });

                if ([200, 201].indexOf(result.status) !== -1) {
                    const { data } = result;

                    return data;
                } else {
                    onRequestDefaultError({ response: result });
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        },
        async remove(code, type) {
            try {
                const result = await this.$http({
                    method: 'DELETE',
                    url: `/dict/${type}/${ code }`,
                    signal: requests.signal('dictionary-remove', code)
                });

                if (result.status === 200) {
                    const { data } = result;

                    return data;
                } else {
                    onRequestDefaultError({ response: result });
                }
            } catch (e) {
                onRequestDefaultError(e);
            }
        }
    }
});

export default useDictApi;
